import React, { useState } from 'react';
import styled from 'styled-components';
import { Roster } from '../types/fantasy';
import { Check } from 'lucide-react';
import { theme } from '../styles/theme';

const Container = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  font-family: ${theme.typography.fontFamily};
`;

const Title = styled.h2`
  color: white;
  font-size: ${theme.typography.sizes['3xl']};
  margin: 0 0 0.5rem 0;
  text-align: center;
  font-weight: ${theme.typography.weights.bold};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.lg};
  margin: 0 0 1.5rem 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 2rem;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(233, 213, 255, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(147, 51, 234, 0.4);
    border-radius: 3px;
  }
`;

const TeamCard = styled.div<{ $selected: boolean }>`
  padding: 16px;
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  border: 2px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  position: relative;
  text-align: center;
  box-shadow: ${props => props.$selected ? '0 8px 32px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.2)'};
  
  &:hover {
    background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.15)'};
    border-color: ${props => props.$selected ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-4px);
    box-shadow: ${props => props.$selected ? '0 12px 40px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.3)'};
  }
`;

const TeamName = styled.div<{ $selected: boolean }>`
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const CheckIcon = styled.div<{ $selected: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: ${theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: ${props => props.$selected ? 1 : 0};
  transition: all ${theme.transitions.fast};
  box-shadow: ${theme.shadows.sm};
`;

const Button = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: rgba(99, 102, 241, 0.9);
  backdrop-filter: blur(20px);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-family: ${theme.typography.fontFamily};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
    border-color: rgba(79, 70, 229, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  }
  
  &:disabled {
    background: rgba(209, 213, 219, 0.6);
    color: rgba(107, 114, 128, 0.8);
    border-color: rgba(209, 213, 219, 0.3);
    cursor: not-allowed;
    transform: none;
    opacity: 0.6;
  }
`;

interface Props {
  rosters: Roster[];
  onSelect: (roster: Roster) => void;
}

export const TeamSelector: React.FC<Props> = ({ rosters, onSelect }) => {
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

  const handleNext = () => {
    const roster = rosters.find(r => r.userId === selectedTeam);
    if (roster) {
      onSelect(roster);
    }
  };

  return (
    <Container>
      <Title>🏈 Pick YOUR Team</Title>
      <Subtitle>Click your team below, then click "Next" to continue</Subtitle>
      <TeamsGrid>
        {rosters.map(roster => (
          <TeamCard 
            key={roster.userId}
            $selected={roster.userId === selectedTeam}
            onClick={() => setSelectedTeam(roster.userId)}
          >
            <TeamName $selected={roster.userId === selectedTeam}>{roster.teamName}</TeamName>
            <CheckIcon $selected={roster.userId === selectedTeam}>
              <Check size={14} color="white" />
            </CheckIcon>
          </TeamCard>
        ))}
      </TeamsGrid>
      <Button 
        onClick={handleNext}
        disabled={!selectedTeam}
      >
        Next
      </Button>
    </Container>
  );
};
